import React, { useContext } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useItemSettings } from '@hooks/useItemSettings'
import { AppContext } from '@state/context'
import { closeLastPanel } from '@state/actions/panelsActions'
import {
  PANELS,
  PANEL_LAYOUT_TYPES,
  PANEL_TRANSITION_TYPES,
} from '@helpers/constants'
import { useResponsive } from '@hooks/useResponsive'
import Panel from './Panel'
import Overlay from '@components/Overlay'

import CreateSectionPanel from '@views/Panels/CreateSectionPanel'
import ViewBookPanel from '@views/Panels/ViewBookPanel'
import CreateBookPanel from './CreateBookPanel'
import AddSectionToBookPanel from './AddSectionToBookPanel'
import EditSectionPanel from './EditSectionPanel'
import EditBookPanel from './EditBookPanel'
import ViewSubjectAreaPanel from './ViewSubjectAreaPanel'
import TeachersGuidePanel from './TeachersGuidePanel'
import RemoveClassPanel from './RemoveClassPanel'
import AddStudentToClassPanel from './AddStudentToSchoolResource/AddStudentToSchoolClassPanel'
import ShareBookPanel from './ShareBookPanel'
import EditExercisePanel from './EditExercisePanel'
import ViewSectionPanel from './ViewSectionPanel'
import {
  clearProgressInspectionStuff,
  setEditBookPanelStartOnTab,
} from '@state/actions/appActions'
import CreateStudentPanel from './CreateStudentPanel'
import ManageStudentsPanel from './ManageStudentsPanel'
import FeedbackPanel from './FeedbackPanel'
import SearchPanel from './SearchPanel'
import UserAgreementPanel from './UserAgreementPanel'
import AddStudentToSchoolGroupPanel from './AddStudentToSchoolResource/AddStudentToSchoolGroupPanel'
import AddClassPanel from './AddClassPanel'
import CreateSchoolGroupPanel from './CreateSchoolGroupPanel'
import EditSchoolGroupPanel from './EditSchoolGroupPanel'
import AddStudentToDemoSchoolGroupPanel from './AddStudentToSchoolResource/AddStudentToDemoSchoolGroupPanel'
import MigrateMySchoolClassPanel from './MigrateMySchoolClass'
import HandleStudentPassword from './HandleStudentPassword'
import RegisterStudentsPanel from './RegisterStudents'
import DeleteMySchoolClassPanel from './DeleteMySchoolClass'
import CreateExamPanel from './CreateExamPanel'
import EditExamPanel from './EditExamPanel'
import InspectProgressPanel from '@views/Panels/InspectProgressPanel'

const Panels = () => {
  const [state, dispatch] = useContext(AppContext)
  const {
    app: {
      selectedBookForProgressInspection,
      selectedSectionForProgressInspection,
    },
    panels: { openPanels },
  } = state

  const { breakpoints } = useResponsive()

  // Invoke useItemSettings here to make sure values are loaded for panels
  const {} = useItemSettings()

  const modalWidth = 'calc(100% - 4rem)'

  return (
    <>
      <Panel
        visible={openPanels.includes(PANELS.CREATE_SECTION)}
        transitionType={
          breakpoints.mobile
            ? PANEL_TRANSITION_TYPES.FROM_BOTTOM
            : PANEL_TRANSITION_TYPES.FROM_RIGHT
        }
        width={breakpoints.mobile ? '100%' : '600px'}
      >
        <CreateSectionPanel />
      </Panel>

      <Panel
        visible={openPanels.includes(PANELS.EDIT_SECTION)}
        transitionType={
          breakpoints.mobile
            ? PANEL_TRANSITION_TYPES.FROM_BOTTOM
            : PANEL_TRANSITION_TYPES.FROM_RIGHT
        }
        width={breakpoints.mobile ? '100%' : '600px'}
      >
        <EditSectionPanel />
      </Panel>

      <Panel
        visible={openPanels.includes(PANELS.VIEW_SECTION)}
        transitionType={PANEL_TRANSITION_TYPES.FROM_RIGHT}
        width={breakpoints.mobile ? '100%' : '400px'}
      >
        <ViewSectionPanel />
      </Panel>

      <Panel
        visible={openPanels.includes(PANELS.VIEW_BOOK)}
        transitionType={PANEL_TRANSITION_TYPES.FROM_RIGHT}
        width={breakpoints.mobile ? '100%' : '400px'}
      >
        <ViewBookPanel />
      </Panel>

      <Panel
        visible={openPanels.includes(PANELS.CREATE_BOOK)}
        transitionType={
          breakpoints.mobile
            ? PANEL_TRANSITION_TYPES.FROM_BOTTOM
            : PANEL_TRANSITION_TYPES.FROM_RIGHT
        }
        width={breakpoints.mobile ? '100%' : '600px'}
      >
        <CreateBookPanel />
      </Panel>

      <Panel
        visible={openPanels.includes(PANELS.EDIT_BOOK)}
        transitionType={
          breakpoints.mobile
            ? PANEL_TRANSITION_TYPES.FROM_BOTTOM
            : PANEL_TRANSITION_TYPES.FROM_RIGHT
        }
        width={breakpoints.mobile ? '100%' : '600px'}
      >
        <EditBookPanel />
      </Panel>

      <Panel
        visible={openPanels.includes(PANELS.FEEDBACK)}
        transitionType={
          breakpoints.mobile
            ? PANEL_TRANSITION_TYPES.FROM_BOTTOM
            : PANEL_TRANSITION_TYPES.FROM_RIGHT
        }
        width={breakpoints.mobile ? '100%' : '400px'}
      >
        <FeedbackPanel />
      </Panel>

      <Panel
        visible={openPanels.includes(PANELS.ADD_SECTION_TO_BOOK)}
        transitionType={PANEL_TRANSITION_TYPES.FADE}
        layoutType={PANEL_LAYOUT_TYPES.MODAL}
        width={modalWidth}
        shadow
      >
        <AddSectionToBookPanel />
      </Panel>

      <Panel
        visible={openPanels.includes(PANELS.VIEW_SUBJECT_AREA)}
        transitionType={
          breakpoints.mobile
            ? PANEL_TRANSITION_TYPES.FROM_BOTTOM
            : PANEL_TRANSITION_TYPES.FROM_RIGHT
        }
        width={breakpoints.mobile ? '100%' : '600px'}
      >
        <ViewSubjectAreaPanel />
      </Panel>

      <Panel
        visible={openPanels.includes(PANELS.TEACHERS_GUIDE)}
        transitionType={
          breakpoints.mobile
            ? PANEL_TRANSITION_TYPES.FROM_BOTTOM
            : PANEL_TRANSITION_TYPES.FROM_RIGHT
        }
        width={breakpoints.mobile ? '100%' : '600px'}
      >
        <TeachersGuidePanel />
      </Panel>

      <Panel
        visible={openPanels.includes(PANELS.ADD_SCHOOL_CLASS)}
        transitionType={PANEL_TRANSITION_TYPES.FADE}
        layoutType={PANEL_LAYOUT_TYPES.MODAL}
        width={modalWidth}
        shadow
      >
        <AddClassPanel />
      </Panel>

      <Panel
        visible={openPanels.includes(PANELS.REMOVE_SCHOOL_CLASS)}
        transitionType={PANEL_TRANSITION_TYPES.FADE}
        layoutType={PANEL_LAYOUT_TYPES.MODAL}
        width={modalWidth}
        shadow
      >
        <RemoveClassPanel />
      </Panel>

      <Panel
        visible={openPanels.includes(PANELS.ADD_STUDENT_TO_SCHOOL_CLASS)}
        transitionType={PANEL_TRANSITION_TYPES.FADE}
        layoutType={PANEL_LAYOUT_TYPES.MODAL}
        width={modalWidth}
        shadow
      >
        <AddStudentToClassPanel />
      </Panel>

      <Panel
        visible={openPanels.includes(PANELS.CREATE_SCHOOL_GROUP)}
        transitionType={
          breakpoints.mobile
            ? PANEL_TRANSITION_TYPES.FROM_BOTTOM
            : PANEL_TRANSITION_TYPES.FROM_RIGHT
        }
        width={breakpoints.mobile ? '100%' : '600px'}
      >
        <CreateSchoolGroupPanel />
      </Panel>

      <Panel
        visible={openPanels.includes(PANELS.EDIT_SCHOOL_GROUP)}
        transitionType={
          breakpoints.mobile
            ? PANEL_TRANSITION_TYPES.FROM_BOTTOM
            : PANEL_TRANSITION_TYPES.FROM_RIGHT
        }
        width={breakpoints.mobile ? '100%' : '600px'}
      >
        <EditSchoolGroupPanel />
      </Panel>

      <Panel
        visible={openPanels.includes(PANELS.ADD_STUDENT_TO_SCHOOL_GROUP)}
        transitionType={PANEL_TRANSITION_TYPES.FADE}
        layoutType={PANEL_LAYOUT_TYPES.MODAL}
        width={modalWidth}
        shadow
      >
        <AddStudentToSchoolGroupPanel />
      </Panel>

      <Panel
        visible={openPanels.includes(PANELS.ADD_STUDENT_TO_DEMO_SCHOOL_GROUP)}
        transitionType={PANEL_TRANSITION_TYPES.FADE}
        layoutType={PANEL_LAYOUT_TYPES.MODAL}
        width={modalWidth}
        shadow
      >
        <AddStudentToDemoSchoolGroupPanel />
      </Panel>

      <Panel
        visible={openPanels.includes(PANELS.SHARE_BOOK)}
        transitionType={PANEL_TRANSITION_TYPES.FADE}
        layoutType={PANEL_LAYOUT_TYPES.MODAL}
        width={modalWidth}
        shadow
      >
        <ShareBookPanel />
      </Panel>

      <Panel
        visible={openPanels.includes(PANELS.MANAGE_STUDENTS)}
        transitionType={PANEL_TRANSITION_TYPES.FADE}
        layoutType={PANEL_LAYOUT_TYPES.MODAL}
        width={modalWidth}
        shadow
      >
        <ManageStudentsPanel />
      </Panel>

      <Panel
        visible={openPanels.includes(PANELS.CREATE_STUDENT)}
        transitionType={
          breakpoints.mobile
            ? PANEL_TRANSITION_TYPES.FROM_BOTTOM
            : PANEL_TRANSITION_TYPES.FROM_RIGHT
        }
        width={breakpoints.mobile ? '100%' : '600px'}
      >
        <CreateStudentPanel />
      </Panel>

      <Panel
        visible={openPanels.includes(PANELS.SEARCH)}
        width={breakpoints.wide ? '50%' : '40%'}
        transitionType={PANEL_TRANSITION_TYPES.FROM_LEFT}
        shadow={true}
      >
        <SearchPanel />
      </Panel>

      <Panel
        visible={openPanels.includes(PANELS.USER_AGREEMENT)}
        transitionType={PANEL_TRANSITION_TYPES.FADE}
        layoutType={PANEL_LAYOUT_TYPES.MODAL}
        width={'min(600px, calc(100% - 4rem))'}
        shadow
      >
        <UserAgreementPanel />
      </Panel>

      <Panel
        visible={openPanels.includes(PANELS.MIGRATE_MY_SCHOOL_CLASS)}
        transitionType={PANEL_TRANSITION_TYPES.FADE}
        layoutType={PANEL_LAYOUT_TYPES.MODAL}
        width={'min(600px, calc(100% - 4rem))'}
        shadow
      >
        <MigrateMySchoolClassPanel />
      </Panel>

      <Panel
        visible={openPanels.includes(PANELS.DELETE_MY_SCHOOL_CLASS)}
        transitionType={PANEL_TRANSITION_TYPES.FADE}
        layoutType={PANEL_LAYOUT_TYPES.MODAL}
        width={'min(600px, calc(100% - 4rem))'}
        shadow
      >
        <DeleteMySchoolClassPanel />
      </Panel>

      <Panel
        visible={openPanels.includes(PANELS.HANDLE_STUDENT_PASSWORD)}
        transitionType={PANEL_TRANSITION_TYPES.FADE}
        layoutType={PANEL_LAYOUT_TYPES.MODAL}
        width={'min(600px, calc(100% - 4rem))'}
        shadow
      >
        <HandleStudentPassword />
      </Panel>

      <Panel
        visible={openPanels.includes(PANELS.REGISTER_STUDENTS)}
        transitionType={PANEL_TRANSITION_TYPES.FADE}
        layoutType={PANEL_LAYOUT_TYPES.MODAL}
        width={'min(600px, calc(100% - 4rem))'}
        shadow
      >
        <RegisterStudentsPanel />
      </Panel>

      <Panel
        visible={openPanels.includes(PANELS.CREATE_EXAM)}
        transitionType={
          breakpoints.mobile
            ? PANEL_TRANSITION_TYPES.FROM_BOTTOM
            : PANEL_TRANSITION_TYPES.FROM_RIGHT
        }
        width={breakpoints.mobile ? '100%' : '600px'}
      >
        <CreateExamPanel />
      </Panel>

      <Panel
        visible={openPanels.includes(PANELS.EDIT_EXAM)}
        width={`${state?.panels?.sideBarWidth}px` ?? '40%'}
        transitionType={PANEL_TRANSITION_TYPES.FROM_LEFT}
        shadow={true}
      >
        <EditExamPanel />
      </Panel>

      <Panel
        visible={openPanels.includes(PANELS.INSPECT_PROGRESS)}
        transitionType={
          breakpoints.mobile
            ? PANEL_TRANSITION_TYPES.FROM_BOTTOM
            : PANEL_TRANSITION_TYPES.FROM_RIGHT
        }
        width={breakpoints.mobile ? '100%' : '480px'}
      >
        <InspectProgressPanel />
      </Panel>

      <EditExercisePanel openPanels={openPanels} />

      <AnimatePresence>
        {openPanels.length > 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <Overlay
              handleClick={() => {
                clearProgressInspectionStuff()(dispatch)

                if (openPanels.at(-1) !== PANELS.USER_AGREEMENT) {
                  dispatch(setEditBookPanelStartOnTab(null))
                  dispatch(closeLastPanel())
                }
              }}
            />
            {/* And here we need to set overflow hidden on body
                        Whats the tailwind way of doing that? */}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  )
}

export default Panels
